<template>
  <section class="container mx-auto px-4 md:px-8 py-10">
    <div class="mb-6 flex items-end justify-between gap-3">
      <h1 class="text-2xl md:text-3xl font-bold">Бажане</h1>
      <NuxtLink to="/catalog" class="underline hover:opacity-80"
        >Продовжити покупки</NuxtLink
      >
    </div>

    <div v-if="!keys.length" class="text-gray-600">
      Список бажаного порожній.
    </div>

    <div v-else>
      <CatalogProductGrid :items="items" :loading="loading" density="roomy" />
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useNuxtApp } from "#app";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  documentId,
  getDocs,
} from "firebase/firestore";
import { useWishlistStore } from "~/stores/wishlist";
import { sortGridItems } from "~/composables/catalog/gridItems";

const wl = useWishlistStore();
const keys = computed(() => wl.ids); // тепер це ["pid:vid", ...]

const loading = ref(false);
const items = ref([]); // [{ key, product, variant }]

const { $db } = useNuxtApp();

const parseKey = (k) => {
  const i = String(k).indexOf(":");
  if (i <= 0 || i === k.length - 1) return null;
  return [k.slice(0, i), k.slice(i + 1)];
};

function chunk(arr, n) {
  return Array.from({ length: Math.ceil(arr.length / n) }, (_, i) =>
    arr.slice(i * n, i * n + n),
  );
}

async function fetchProductsByIds(productIds) {
  const batches = chunk(productIds, 10);
  const map = new Map();
  for (const b of batches) {
    const snap = await getDocs(
      query(collection($db, "products"), where(documentId(), "in", b)),
    );
    snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  }
  return map;
}

onMounted(async () => {
  if (!wl.ready) await wl.init();
  if (!keys.value.length) return;

  loading.value = true;
  try {
    const pairs = keys.value.map(parseKey).filter(Boolean); // [[pid, vid], ...]
    const pids = Array.from(new Set(pairs.map(([pid]) => pid)));
    const pMap = await fetchProductsByIds(pids);

    const results = [];
    const foundKeys = new Set();
    await Promise.all(
      pairs.map(async ([pid, vid]) => {
        const p = pMap.get(pid);
        if (!p) return;
        try {
          const vsnap = await getDoc(
            doc($db, "products", pid, "variants", vid),
          );
          if (vsnap.exists()) {
            foundKeys.add(`${pid}:${vid}`);
            results.push({
              key: `${pid}__${vid}`,
              product: p,
              variant: { id: vsnap.id, ...vsnap.data() },
            });
          }
        } catch {}
      }),
    );

    // зберігаємо первинний порядок keys
    const order = new Map(keys.value.map((k, i) => [k, i]));
    const ordered = results
      .slice()
      .sort(
        (a, b) =>
          (order.get(`${a.product.id}:${a.variant.id}`) ?? 0) -
          (order.get(`${b.product.id}:${b.variant.id}`) ?? 0),
      );

    // Ранг (звичайні → ексклюзив → без наявності) спільний із каталогом,
    // але всередині рангу тут значущий порядок додавання в бажане —
    // саме для цього в sortGridItems є режим keep-order.
    items.value = sortGridItems(ordered, "keep-order");

    // Auto-clean stale keys (removed products/variants)
    const stale = (keys.value || []).filter((k) => !foundKeys.has(String(k)));
    if (stale.length) {
      // remove locally + from server (if logged in)
      await wl.removeKeys(stale);
    }
  } finally {
    loading.value = false;
  }
});
</script>
